import { DEAL_FIELDS, PROJECT_FIELDS } from './boards.js';
import { CANDIDATE_FIELDS } from './candidates.js';
import { JOB_FIELDS } from './jobs-records.js';
/**
 * Merge fields for pipeline trigger actions and note templates: `{{entity.field}}` in a task
 * title, a description or a note body, filled from a bundle of plain values (the worker's for a
 * run, the browser's for a note). Custom fields are `{{<entity>.custom.<key>}}`: Automations
 * accept only `candidate.custom` (`unknownTemplatePlaceholders`), a note template on one kind
 * accepts that kind's (`unknownNoteTemplateFields`). No logic, loops or filters: a template is
 * text with holes, so it cannot run anything.
 */
export const TEMPLATE_ENTITIES = [
    'candidate',
    'deal',
    'job',
    'project',
    'company',
    'contact',
];
/**
 * The fields a template may name, per entity, with their field type: each entity's FIELDS map.
 * Companies and contacts have no filter catalogue yet, so theirs are spelled out here.
 * Custom fields (`custom.<key>`) are checked against the organisation's definitions, not here.
 */
export const TEMPLATE_FIELD_TYPES = {
    // Sensitive until the compliance decision: never sent anywhere a template's output goes.
    candidate: Object.fromEntries(Object.entries(CANDIDATE_FIELDS).filter(([k]) => k !== 'dateOfBirth' && k !== 'gender')),
    deal: DEAL_FIELDS,
    job: JOB_FIELDS,
    project: PROJECT_FIELDS,
    company: {
        id: 'reference',
        name: 'text',
        domain: 'text',
        website: 'url',
        industry: 'text',
        country: 'text',
        phone: 'phone',
        ownerId: 'user',
    },
    contact: {
        id: 'reference',
        firstName: 'text',
        lastName: 'text',
        email: 'email',
        phone: 'phone',
        altPhone: 'phone',
        jobTitle: 'text',
        companyId: 'reference',
        ownerId: 'user',
    },
};
const keysOf = (e) => Object.keys(TEMPLATE_FIELD_TYPES[e]);
export const TEMPLATE_FIELDS = {
    candidate: keysOf('candidate'),
    deal: keysOf('deal'),
    job: keysOf('job'),
    project: keysOf('project'),
    company: keysOf('company'),
    contact: keysOf('contact'),
};
/**
 * The entities a trigger's templates can name, by its subject: what the worker loads for a run
 * (the subject, its job, project, company and the project's primary contact). A Candidate Flow
 * candidate belongs to no project.
 */
export const TEMPLATE_ENTITIES_BY_SUBJECT = {
    project: ['project', 'company', 'contact'],
    deal: ['deal', 'project', 'company', 'contact'],
    job: ['job', 'project', 'company', 'contact'],
    candidate: ['candidate'],
    shortlist: ['candidate', 'job', 'project', 'company', 'contact'],
};
const PLACEHOLDER = /\{\{\s*([a-zA-Z][\w]*(?:\.[a-zA-Z_][\w-]*){1,2})\s*\}\}/g;
/** Every distinct `entity.field` (or `entity.custom.key`) a template names, in order of use. */
export function templatePlaceholders(template) {
    return [...new Set([...template.matchAll(PLACEHOLDER)].map((m) => m[1]))];
}
/** The placeholders a template names that are not an entity's native field (custom ones are kept). */
export function unknownTemplatePlaceholders(template) {
    return templatePlaceholders(template).filter((p) => {
        const [entity, field, key] = p.split('.');
        if (!TEMPLATE_ENTITIES.includes(entity))
            return true;
        if (field === 'custom')
            return entity !== 'candidate' || !key;
        return key !== undefined || !TEMPLATE_FIELDS[entity].includes(field);
    });
}
function text(value) {
    if (value === null || value === undefined)
        return '';
    if (Array.isArray(value))
        return value.map(text).join(', ');
    if (value instanceof Date)
        return value.toISOString();
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    return JSON.stringify(value) ?? '';
}
/** Backslash-escapes what would turn text into a link, image, HTML or emphasis in Markdown. */
export const escapeMarkdown = (s) => s.replace(/[\\`*_[\]<>]/g, '\\$&');
/** Escapes what would turn text into markup in HTML. */
const HTML_ENTITY = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
export const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => HTML_ENTITY[c] ?? c);
/**
 * Fill a template. A placeholder the bundle cannot answer renders empty and is reported in
 * `missing`; an empty value that exists (null, '') is not missing.
 *
 * `markdown` / `html`: the output is rendered as Markdown (an Automation's note body or task
 * description) or HTML (a note template's fill), so each value is escaped: record data stays text, and only the template's own
 * formatting formats.
 */
export function renderTemplate(template, bundle, opts = {}) {
    const missing = new Set();
    const out = template.replace(PLACEHOLDER, (_, path) => {
        const [entity, field, key] = path.split('.');
        const record = bundle[entity];
        const source = (key === undefined ? record : field === 'custom' ? record?.custom : undefined);
        const name = key ?? field;
        if (!source || !Object.prototype.hasOwnProperty.call(source, name)) {
            missing.add(path);
            return '';
        }
        const value = text(source[name]);
        if (opts.html)
            return escapeHtml(value);
        return opts.markdown ? escapeMarkdown(value) : value;
    });
    return { text: out, missing: [...missing] };
}
//# sourceMappingURL=templates.js.map